import { Timestamp } from '@bufbuild/protobuf';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'sonner';
import { PROGRAM_ISSUES_KEY, PROGRAM_RISKS_KEY } from '../../../components/delivery/widgets/useProgramRisksIssues';
import { deliveryClient } from '../../../lib/connect/clients';
import { withNoneOption } from '../_shared/api/selectOptions';
import { makeEntityHooks } from '../_shared/hooks/makeEntityHooks';
import { useDuties } from '../Deliverables/hooks';
// The "Linked deliverable" options of a risk or an issue: the program's
// contract-model deliverables (D113), by title, never an id.
export function useDeliverableLinkOptions(programId) {
    const duties = useDuties(programId).data?.duties;
    return useMemo(()=>withNoneOption((duties ?? []).map((d)=>({
                value: d.id,
                label: `${d.title || 'Untitled deliverable'}${d.pwsRef ? ` · PWS ${d.pwsRef}` : ''}`
            }))), [
        duties
    ]);
}
// The occurrence link a save sends: kept (omitted) while the deliverable is
// unchanged, cleared when it changes, since a date belongs to one
// deliverable and pangaea refuses a pairing that is not.
export function occurrenceLinkFor(storedDutyId, nextDutyId) {
    return (storedDutyId ?? '') === nextDutyId ? undefined : '';
}
// ============================================================================
// Risk — Upsert / List / Delete
// ============================================================================
export const riskHooks = makeEntityHooks({
    key: 'risk',
    list: async (req)=>{
        const resp = await deliveryClient.listRisks(req);
        return {
            items: resp.risks
        };
    },
    upsert: async (req)=>{
        const resp = await deliveryClient.upsertRisk(req);
        return resp.risk;
    },
    remove: async (id)=>{
        await deliveryClient.deleteRisk({
            id
        });
    },
    entityId: (r)=>r.id,
    // Keep the RiskIssueList summary (queries PROGRAM_RISKS_KEY) live after a
    // risk is created/edited on the tab.
    extraInvalidateKeys: [
        PROGRAM_RISKS_KEY
    ]
});
// ============================================================================
// Risk — Convert to Issue
// ============================================================================
export function useConvertRiskToIssue() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (riskId)=>deliveryClient.convertRiskToIssue({
                riskId
            }),
        onSuccess: (resp)=>{
            qc.invalidateQueries({
                queryKey: [
                    'risk'
                ]
            });
            qc.invalidateQueries({
                queryKey: [
                    'issue'
                ]
            });
            qc.invalidateQueries({
                queryKey: [
                    PROGRAM_RISKS_KEY
                ]
            });
            qc.invalidateQueries({
                queryKey: [
                    PROGRAM_ISSUES_KEY
                ]
            });
            toast.success(resp.created ? 'Converted to Issue' : 'Already converted - showing the existing Issue');
        },
        onError: (err)=>{
            toast.error(`Failed to convert to Issue: ${err.message}`);
        }
    });
}
// ============================================================================
// Risk — Escalate to Leadership
// ============================================================================
export function useEscalateRiskToLeadership() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (riskId)=>deliveryClient.escalateRiskToLeadership({
                id: riskId
            }),
        // Same invalidation footprint as useConvertRiskToIssue above, since both
        // are deliberate risk actions surfaced on the same row.
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    'risk'
                ]
            });
            qc.invalidateQueries({
                queryKey: [
                    'issue'
                ]
            });
            qc.invalidateQueries({
                queryKey: [
                    PROGRAM_RISKS_KEY
                ]
            });
            qc.invalidateQueries({
                queryKey: [
                    PROGRAM_ISSUES_KEY
                ]
            });
            toast.success('Escalated to leadership');
        },
        onError: (err)=>{
            toast.error(`Failed to escalate to leadership: ${err.message}`);
        }
    });
}
// ============================================================================
// Issue — Upsert / List / Delete
// ============================================================================
export const issueHooks = makeEntityHooks({
    key: 'issue',
    list: async (req)=>{
        const resp = await deliveryClient.listIssues(req);
        return {
            items: resp.issues
        };
    },
    upsert: async (req)=>{
        const resp = await deliveryClient.upsertIssue(req);
        return resp.issue;
    },
    remove: async (id)=>{
        await deliveryClient.deleteIssue({
            id
        });
    },
    entityId: (i)=>i.id,
    // Keep the RiskIssueList summary (queries PROGRAM_ISSUES_KEY) live after an
    // issue is created/edited on the tab.
    extraInvalidateKeys: [
        PROGRAM_ISSUES_KEY
    ]
});
// ============================================================================
// CustomerTouchpoint — Upsert (create/edit) / List
// ============================================================================
const TOUCHPOINT_KEY = 'customer touchpoint';
export function useCustomerTouchpoints(programId) {
    return useQuery({
        queryKey: [
            TOUCHPOINT_KEY,
            'list',
            programId
        ],
        queryFn: async ()=>{
            if (!programId) return {
                items: []
            };
            const resp = await deliveryClient.listCustomerTouchpoints({
                programId
            });
            return {
                items: resp.touchpoints
            };
        },
        enabled: !!programId,
        staleTime: 30000
    });
}
export function useUpsertCustomerTouchpoint() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input)=>{
            const resp = await deliveryClient.upsertCustomerTouchpoint({
                id: input.id,
                programId: input.programId,
                method: input.method,
                // Briefing level and provided-by/to are no longer collected by the
                // form (SAD-163: touchpoints are now an internal meeting-note
                // recorder, replaced by the plain attendees list below). The handler
                // ignores these fields on both create and update, preserving
                // whatever is already stored — sent empty only for wire compatibility.
                briefingLevel: '',
                dateOfBriefing: Timestamp.fromDate(input.dateOfBriefing),
                briefDescription: input.briefDescription,
                providedBy: '',
                providedByContactId: '',
                providedToContactId: '',
                providedToSecondaryId: '',
                providedToPrimary: '',
                providedToSecondary: '',
                comments: input.comments,
                summaryAndActionItems: input.summaryAndActionItems,
                attendees: input.attendees
            });
            return resp.touchpoint;
        },
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    TOUCHPOINT_KEY
                ]
            });
            toast.success('Touchpoint saved');
        },
        onError: (err)=>{
            toast.error(`Failed to save touchpoint: ${err.message}`);
        }
    });
}
// ============================================================================
// GrowthOpportunity — Upsert / List / Delete
// ============================================================================
export const growthHooks = makeEntityHooks({
    key: 'growth opportunity',
    list: async (req)=>{
        const resp = await deliveryClient.listGrowthOpportunities(req);
        return {
            items: resp.opportunities
        };
    },
    upsert: async (req)=>{
        const resp = await deliveryClient.upsertGrowthOpportunity(req);
        return resp.opportunity;
    },
    remove: async (id)=>{
        await deliveryClient.deleteGrowthOpportunity({
            id
        });
    },
    entityId: (g)=>g.id
});
// ============================================================================
// SubcontractorReview — Upsert / List / Delete
// ============================================================================
const SUB_REVIEW_KEY = 'subcontractor review';
export function useSubcontractorReviews(programId) {
    return useQuery({
        queryKey: [
            SUB_REVIEW_KEY,
            'list',
            programId
        ],
        queryFn: async ()=>{
            if (!programId) return {
                items: []
            };
            const resp = await deliveryClient.listSubcontractorReviews({
                programId,
                companyAssignmentId: ''
            });
            return {
                items: resp.reviews
            };
        },
        enabled: !!programId,
        staleTime: 30000
    });
}
export function useUpsertSubcontractorReview() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input)=>{
            const resp = await deliveryClient.upsertSubcontractorReview({
                id: input.id,
                programId: input.programId,
                companyAssignmentId: input.companyAssignmentId,
                reviewDate: Timestamp.fromDate(input.reviewDate),
                dueDate: Timestamp.fromDate(input.dueDate),
                score: input.score,
                feedback: input.feedback,
                state: input.state,
                reviewedBy: input.reviewedBy
            });
            return resp.review;
        },
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    SUB_REVIEW_KEY
                ]
            });
            toast.success('Subcontractor review saved');
        },
        onError: (err)=>{
            toast.error(`Failed to save subcontractor review: ${err.message}`);
        }
    });
}
export function useDeleteSubcontractorReview() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id)=>{
            await deliveryClient.deleteSubcontractorReview({
                id
            });
        },
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    SUB_REVIEW_KEY
                ]
            });
            toast.success('Subcontractor review deleted');
        },
        onError: (err)=>{
            toast.error(`Failed to delete subcontractor review: ${err.message}`);
        }
    });
}
